import { Link } from 'react-router-dom';

interface Event {
  id: string;
  title: string;
  category: string;
  start_date: string;
  location_type: string;
  location_city: string | null;
  location_country: string | null;
  price: number | null;
  currency: string | null;
  cover_image: string | null;
  registrations?: { count: number }[];
}

interface EventsGridProps {
  events: Event[];
  isRegisteredMap: Set<string>;
  viewMode: 'grid' | 'list';
}

const categoryColors: Record<string, { bg: string; text: string }> = {
  Music: { bg: 'bg-pink-100', text: 'text-pink-700' },
  Technology: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  Business: { bg: 'bg-orange-100', text: 'text-orange-700' },
  Sports: { bg: 'bg-green-100', text: 'text-green-700' },
  Arts: { bg: 'bg-rose-100', text: 'text-rose-700' },
  Food: { bg: 'bg-amber-100', text: 'text-amber-700' },
};

const getCategoryStyle = (cat: string) =>
  categoryColors[cat] || { bg: 'bg-teal-100', text: 'text-teal-700' };

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', JPY: '¥',
  NGN: '₦', GHS: 'GH₵', KES: 'KSh', ZAR: 'R', UGX: 'USh',
  TZS: 'TSh', RWF: 'FRw', ETB: 'Br', EGP: 'E£', MAD: 'MAD',
  XOF: 'CFA', XAF: 'FCFA', BRL: 'R$', MXN: 'MX$', INR: '₹',
  CNY: '¥', SGD: 'S$', AED: 'د.إ', SAR: '﷼',
};

function formatPrice(price: number | null, currency: string | null): string {
  if (!price || price === 0) return 'Free';
  const code = (currency || 'USD').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] || code;
  const formatted = price % 1 === 0 ? price.toLocaleString() : price.toFixed(2);
  const isWordCode = symbol === code;
  return isWordCode ? `${code} ${formatted}` : `${symbol}${formatted}`;
}

const monthNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const EventsGrid = ({ events, isRegisteredMap, viewMode }: EventsGridProps) => {
  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {events.map((event) => {
          const date = new Date(event.start_date);
          const month = monthNames[date.getMonth()];
          const day = date.getDate();
          const year = date.getFullYear();
          const location =
            event.location_type === 'online'
              ? 'Online Event'
              : event.location_city || event.location_country || 'Location TBA';
          const attendees = event.registrations?.[0]?.count || 0;
          const catStyle = getCategoryStyle(event.category);
          const isRegistered = isRegisteredMap.has(event.id);

          return (
            <Link
              key={event.id}
              to={`/event/${event.id}`}
              className="group flex gap-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all overflow-hidden cursor-pointer"
            >
              {/* Image */}
              <div className="relative w-48 flex-shrink-0 overflow-hidden">
                <img
                  src={
                    event.cover_image ||
                    'https://public.readdy.ai/ai/img_res/a6188ef80f3269e5f66ff2c6c4fdffd9.jpg'
                  }
                  alt={event.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {isRegistered && (
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-500 text-white flex items-center gap-1 shadow">
                      <i className="ri-checkbox-circle-fill text-xs"></i>
                      Registered
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 py-5 pr-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${catStyle.bg} ${catStyle.text}`}>
                      {event.category}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      <i className={`${event.location_type === 'online' ? 'ri-global-line' : 'ri-map-pin-line'} mr-1`}></i>
                      {event.location_type === 'online' ? 'Online' : 'In-Person'}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors mb-1 line-clamp-1">
                    {event.title}
                  </h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {location}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line text-teal-500"></i>
                      {day} {month} {year}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-group-line text-teal-500"></i>
                      {attendees} attending
                    </span>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-teal-600">
                      {formatPrice(event.price, event.currency)}
                    </p>
                    {event.price && event.price > 0 && event.currency && (
                      <p className="text-xs text-gray-400">{event.currency.toUpperCase()}</p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
      {events.map((event) => {
        const date = new Date(event.start_date);
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const location =
          event.location_type === 'online'
            ? 'Online Event'
            : event.location_city || event.location_country || 'Location TBA';
        const attendees = event.registrations?.[0]?.count || 0;
        const catStyle = getCategoryStyle(event.category);
        const isRegistered = isRegisteredMap.has(event.id);

        return (
          <Link
            key={event.id}
            to={`/event/${event.id}`}
            className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 cursor-pointer flex flex-col"
          >
            {/* Image */}
            <div className="relative h-52 overflow-hidden flex-shrink-0">
              <img
                src={
                  event.cover_image ||
                  'https://readdy.ai/api/search-image?query=professional%20event%20conference%20gathering%20people%20networking%20modern%20venue%20bright%20lighting&width=600&height=400&seq=event-grid-default&orientation=landscape'
                }
                alt={event.title}
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              {/* Date badge */}
              <div className="absolute top-3 left-3 bg-white rounded-xl px-3 py-2 shadow-lg text-center min-w-[48px]">
                <p className="text-xl font-extrabold text-gray-900 leading-none">{day}</p>
                <p className="text-xs font-bold text-teal-600 mt-0.5">{month}</p>
              </div>
              {/* Category + registered */}
              <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${catStyle.bg} ${catStyle.text} backdrop-blur-sm`}>
                  {event.category}
                </span>
                {isRegistered && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-500 text-white flex items-center gap-1 shadow">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Registered
                  </span>
                )}
              </div>
              {/* Location type */}
              <div className="absolute bottom-3 left-3">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-black/60 text-white backdrop-blur-sm">
                  <i className={`${event.location_type === 'online' ? 'ri-global-line' : 'ri-map-pin-line'} mr-1`}></i>
                  {event.location_type === 'online' ? 'Online' : 'In-Person'}
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 flex flex-col flex-1">
              <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors mb-1.5 line-clamp-2 leading-snug">
                {event.title}
              </h3>
              <p className="text-sm text-gray-500 flex items-center gap-1 mb-4">
                <i className="ri-map-pin-line text-gray-400"></i>
                {location}
              </p>

              <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xl font-extrabold text-teal-600">
                    {formatPrice(event.price, event.currency)}
                  </p>
                  {event.price && event.price > 0 && event.currency && (
                    <p className="text-xs text-gray-400 text-right">{event.currency.toUpperCase()}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-400">
                  <i className="ri-group-line"></i>
                  <span>{attendees}</span>
                </div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default EventsGrid;
